import {
    ICellRendererParams,
    ITextFilterParams,
    IFilterOptionDef,
    ValueFormatterParams,
    IServerSideGetRowsRequest,
    IDateFilterParams,
    ISetFilterParams,
} from "ag-grid-enterprise";
import { Tooltip } from "@mui/material";
import TenantInfoPopover from "../../components/TenantInfoPopover/TenantInfoPopover";

import { BO_CORE_DEFAULT_NUMBER_VALUE, BO_CORE_DEFAULT_VALUE, DEFAULT_BOCORE_DATE } from "../AppUtils";
import { DatesFormat } from "../DateFormats";
import { formatDateTo, getDateDecreasedByDuration, representTimeDuration, utcToLocalDateTime } from "../DateTimeUtils";
import GridHighlightedValue from "../../components/OrderGridV3/GridHighlightedValue/GridHighlightedValue";
import {
    CustomColumnsRequirements,
    IOrderGridCystomColumnDefinition,
    OrderFilterableColumns,
    OrderRowDataV3,
    SimpleFilterModelTypes,
    V3ColumnOption,
} from "./types";
import { getOrdersV3 } from "../../config/axios/apis/order/order";
import { getUniqueLabelTagsQuery } from "../../config/axios/apis/campaign/campaignApi";
import { LabelTagOwnership, LabelTagType } from "../appEnums/AppEnums";
import CopyToClipboard from "../../components/Common/CopyToClipboard/CopyToClipboard";
import { getGridLinkColor } from "../AGGridUtils";

const PATIENT_ADDRESS_TOOLTIP = "We will search and sort only by the primary location";

export const NUMBER_FILTER_OPTIONS: (IFilterOptionDef | SimpleFilterModelTypes)[] = ["equals", "notEqual", "contains", "lessThan", "greaterThan"];
export const NUMBER_FILTER_OPTIONS_V2: (IFilterOptionDef | SimpleFilterModelTypes)[] = ["equals", "notEqual", "lessThan", "greaterThan"];
export const STRING_FILTER_OPTIONS: (IFilterOptionDef | SimpleFilterModelTypes)[] = ["contains", "equals", "notEqual", "blank", "notBlank"];
export const STRING_FILTER_OPTIONS_V2: (IFilterOptionDef | SimpleFilterModelTypes)[] = ["equals", "notEqual", "blank", "notBlank"];
export const DATE_FILTER_OPTIONS: (IFilterOptionDef | SimpleFilterModelTypes)[] = ["equals", "lessThan", "greaterThan", "inRange"];
export const VALUE_LIST_FILTER_OPTIONS: (IFilterOptionDef | SimpleFilterModelTypes)[] = ["contains", "equals", "notEqual", "blank", "notBlank"];

export const filterOptionsMap: { [x in keyof OrderFilterableColumns]: (IFilterOptionDef | SimpleFilterModelTypes)[] } = {
    id: NUMBER_FILTER_OPTIONS,
    masterOrderId: NUMBER_FILTER_OPTIONS,
    samples: STRING_FILTER_OPTIONS,
    kits: STRING_FILTER_OPTIONS,
    comments: STRING_FILTER_OPTIONS,

    orderingOrganizationName: STRING_FILTER_OPTIONS,
    gtmOrganizationName: STRING_FILTER_OPTIONS,
    healthcareOrganizationName: STRING_FILTER_OPTIONS,
    labOrganizationName: STRING_FILTER_OPTIONS,
    supplierOrganizationName: STRING_FILTER_OPTIONS,
    prescriberFirstName: STRING_FILTER_OPTIONS,
    prescriberLastName: STRING_FILTER_OPTIONS,
    prescriberNpiNumber: STRING_FILTER_OPTIONS,
    creatorFirstName: STRING_FILTER_OPTIONS,
    creatorLastName: STRING_FILTER_OPTIONS,
    // customerName: STRING_FILTER_OPTIONS,
    customerFirstName: STRING_FILTER_OPTIONS,
    customerLastName: STRING_FILTER_OPTIONS,
    customerEmail: STRING_FILTER_OPTIONS,
    customerPhone: STRING_FILTER_OPTIONS,
    customerAddressStreet: STRING_FILTER_OPTIONS,
    customerPostalCode: STRING_FILTER_OPTIONS,
    customerAddressCity: STRING_FILTER_OPTIONS,
    customerCounty: STRING_FILTER_OPTIONS,
    patientId: NUMBER_FILTER_OPTIONS,
    patientFirstName: STRING_FILTER_OPTIONS,
    patientLastName: STRING_FILTER_OPTIONS,
    patientEmail: STRING_FILTER_OPTIONS,
    patientPhone: STRING_FILTER_OPTIONS,
    shippingPersonFirstName: STRING_FILTER_OPTIONS,
    shippingPersonLastName: STRING_FILTER_OPTIONS,
    shippingPersonEmail: STRING_FILTER_OPTIONS,
    shippingPersonPhone: STRING_FILTER_OPTIONS,
    shippingCity: STRING_FILTER_OPTIONS,
    shippingState: STRING_FILTER_OPTIONS,
    shippingPostalCode: STRING_FILTER_OPTIONS,
    shippingAddressLine1: STRING_FILTER_OPTIONS,
    shippingAddressLine2: STRING_FILTER_OPTIONS,
    testProductName: STRING_FILTER_OPTIONS,
    testProductLabCode: STRING_FILTER_OPTIONS,
    testOfferedName: STRING_FILTER_OPTIONS,
    testOfferedLabCode: STRING_FILTER_OPTIONS,
    supplyProductName: STRING_FILTER_OPTIONS,
    supplyProductSku: STRING_FILTER_OPTIONS,
    patientAddressStreet: STRING_FILTER_OPTIONS,
    patientAddressCity: STRING_FILTER_OPTIONS,
    patientPostalCode: STRING_FILTER_OPTIONS,
    patientCounty: STRING_FILTER_OPTIONS,

    outcome: VALUE_LIST_FILTER_OPTIONS,

    workflowStepInProgressName: STRING_FILTER_OPTIONS,
    workflowStepInProgressType: STRING_FILTER_OPTIONS,
    workflowCategory: STRING_FILTER_OPTIONS,

    customerCountry: STRING_FILTER_OPTIONS,
    paymentType: VALUE_LIST_FILTER_OPTIONS,
    patientSex: VALUE_LIST_FILTER_OPTIONS,
    patientRace: VALUE_LIST_FILTER_OPTIONS,
    patientEthnicity: VALUE_LIST_FILTER_OPTIONS,
    patientCountry: STRING_FILTER_OPTIONS,

    patientDateOfBirth: DATE_FILTER_OPTIONS,
    resultPostedAt: DATE_FILTER_OPTIONS,
    created: DATE_FILTER_OPTIONS,
    updated: DATE_FILTER_OPTIONS,

    journeyId: NUMBER_FILTER_OPTIONS,

    labelTags: STRING_FILTER_OPTIONS,
};

export const dateCellRenderer = (value, params: ICellRendererParams) => {
    if (value && typeof value !== "undefined") {
        if (value === DEFAULT_BOCORE_DATE) {
            return "";
            // return (
            //     <CopyToClipboard>
            //         <span>n/a</span>
            //     </CopyToClipboard>
            // );
        }

        const columnId = params.column?.getColId();

        if (columnId === "patientDateOfBirth" || columnId?.includes("Birth")) {
            return (
                <CopyToClipboard>
                    <span>{formatDateTo(value, DatesFormat.defaultUSFormat)}</span>{" "}
                </CopyToClipboard>
            );
        }

        return (
            <CopyToClipboard>
                <Tooltip title={formatDateTo(value, DatesFormat.defaultUSDateTimeFormat)}>
                    <span>{formatDateTo(value, DatesFormat.defaultUSFormat)}</span>
                </Tooltip>
            </CopyToClipboard>
        );
    }
    return "";
};

export const orderGridDataDefinitionV3: IOrderGridCystomColumnDefinition[] = [
    { attrKey: "id", atrbType: "number", name: "ID" },
    { attrKey: "masterOrderId", atrbType: "number", name: "Master Order ID" },

    { attrKey: "workflowStepInProgressName", atrbType: "text", name: "Step In Progress Name" },
    { attrKey: "workflowStepInProgressType", atrbType: "text", name: "Step In Progress Type" },
    { attrKey: "workflowStepInProgressTime", atrbType: "date", name: "Time On This Step" },
    { attrKey: "workflowStepInProgressCanSubmit", atrbType: "boolean", name: "Workflow Step In Progress Can Submit" },

    { attrKey: "patientId", atrbType: "number", name: "Patient ID" },
    { attrKey: "patientFirstName", atrbType: "text", name: "Patient First Name" },
    { attrKey: "patientLastName", atrbType: "text", name: "Patient Last Name" },
    { attrKey: "patientDateOfBirth", atrbType: "timestamp", name: "Patient Date Of Birth" },
    { attrKey: "patientEmail", atrbType: "text", name: "Patient Email" },
    { attrKey: "patientPhone", atrbType: "text", name: "Patient Phone" },
    { attrKey: "patientSex", atrbType: "text", name: "Patient Sex" },
    { attrKey: "patientRace", atrbType: "text", name: "Patient Race" },
    { attrKey: "patientEthnicity", atrbType: "text", name: "Patient Ethnicity" },
    { attrKey: "patientAddressStreet", atrbType: "text", name: "Patient Address Street" },
    { attrKey: "patientAddressCity", atrbType: "text", name: "Patient Address City" },
    { attrKey: "patientPostalCode", atrbType: "text", name: "Patient Postal Code" },
    { attrKey: "patientCounty", atrbType: "text", name: "Patient County" },
    { attrKey: "patientCountry", atrbType: "text", name: "Patient Country" },

    { attrKey: "samples", atrbType: "text", name: "Samples" },
    { attrKey: "kits", atrbType: "text", name: "Kits" },
    { attrKey: "comments", atrbType: "text", name: "Comments" },

    { attrKey: "resultPostedAt", atrbType: "timestamp", name: "Resulted Date" },

    { attrKey: "created", atrbType: "timestamp", name: "Order Date" },
    { attrKey: "updated", atrbType: "timestamp", name: "Updated" },

    { attrKey: "prescriberFirstName", atrbType: "text", name: "Provider First Name" },
    { attrKey: "prescriberLastName", atrbType: "text", name: "Provider Last Name" },
    { attrKey: "prescriberNpiNumber", atrbType: "number", name: "Provider NPI" },

    { attrKey: "testOfferedName", atrbType: "text", name: "Test Ordered" },
    { attrKey: "testOfferedLabCode", atrbType: "text", name: "Test Ordered Lab Code" },

    { attrKey: "labOrganizationId", atrbType: "text", name: "Lab Organization Id" },
    { attrKey: "labOrganizationName", atrbType: "text", name: "Lab Organization" },

    { attrKey: "outcome", atrbType: "text", name: "Order Status" },

    { attrKey: "orderingOrganizationId", atrbType: "text", name: "Ordering Organization Id" },
    { attrKey: "orderingOrganizationName", atrbType: "text", name: "Ordering Organization" },

    { attrKey: "healthcareOrganizationId", atrbType: "text", name: "Healthcare Organization Id" },
    { attrKey: "healthcareOrganizationTenantId", atrbType: "text", name: "Healthcare Tenant Id" },
    { attrKey: "healthcareOrganizationName", atrbType: "text", name: "Healthcare Organization" },

    { attrKey: "gtmOrganizationId", atrbType: "text", name: "Go To Market Organization Id" },
    { attrKey: "gtmOrganizationTenantId", atrbType: "text", name: "Go To Market Tenant Id" },
    { attrKey: "gtmOrganizationName", atrbType: "text", name: "Go To Market Organization" },

    { attrKey: "supplierOrganizationName", atrbType: "text", name: "Supplier Organization" },

    { attrKey: "creatorFirstName", atrbType: "text", name: "Creator First Name" },
    { attrKey: "creatorLastName", atrbType: "text", name: "Creator Last Name" },

    // { attrKey: "customerName", atrbType: "text", name: "Customer Name" },
    { attrKey: "customerFirstName", atrbType: "text", name: "Customer First Name" },
    { attrKey: "customerLastName", atrbType: "text", name: "Customer Last Name" },
    { attrKey: "customerEmail", atrbType: "text", name: "Customer Email" },
    { attrKey: "customerPhone", atrbType: "text", name: "Customer Phone" },

    { attrKey: "customerAddressStreet", atrbType: "text", name: "Customer Address Street" },
    { attrKey: "customerPostalCode", atrbType: "text", name: "Customer Postal Code" },
    { attrKey: "customerAddressCity", atrbType: "text", name: "Customer Address City" },
    { attrKey: "customerCountry", atrbType: "text", name: "Customer Country" },
    { attrKey: "customerCounty", atrbType: "text", name: "Customer County" },

    { attrKey: "paymentType", atrbType: "text", name: "Payment Type" },

    { attrKey: "shippingPersonFirstName", atrbType: "text", name: "Shipping Person First Name" },
    { attrKey: "shippingPersonLastName", atrbType: "text", name: "Shipping Person Last Name" },
    { attrKey: "shippingPersonEmail", atrbType: "text", name: "Shipping Person Email" },
    { attrKey: "shippingPersonPhone", atrbType: "text", name: "Shipping Person Phone" },

    { attrKey: "shippingCity", atrbType: "text", name: "Shipping City" },
    { attrKey: "shippingState", atrbType: "text", name: "Shipping State" },
    { attrKey: "shippingPostalCode", atrbType: "text", name: "Shipping Postal Code" },
    { attrKey: "shippingAddressLine1", atrbType: "text", name: "Shipping Address Line 1" },
    { attrKey: "shippingAddressLine2", atrbType: "text", name: "Shipping Address Line 2" },

    { attrKey: "testProductName", atrbType: "text", name: "Product" },
    { attrKey: "testProductLabCode", atrbType: "text", name: "Product Lab Code" },

    { attrKey: "workflowCategory", atrbType: "text", name: "Workflow Category" },

    { attrKey: "supplyProductName", atrbType: "text", name: "Supply" },
    { attrKey: "supplyProductSku", atrbType: "text", name: "Supply SKU" },

    { attrKey: "labelTags", atrbType: "text", name: "Tags" },

    { attrKey: "isUat", atrbType: "boolean", name: "UAT Order" },
];

export const columnOptions: V3ColumnOption[] = [
    { key: "orderingOrganizationId", options: { suppressColumnsToolPanel: true, hide: true } },
    {
        key: "orderingOrganizationName",
        options: {
            filterParams: { filterOptions: filterOptionsMap.orderingOrganizationName } as ITextFilterParams,
            cellRenderer: (value, params) => (
                <TenantInfoPopover tenantId={params.data.orderingOrganizationTenantId}>
                    <GridHighlightedValue {...params} columnKey="orderingOrganizationName" value={value} enableCopyToClipboard />
                </TenantInfoPopover>
            ),
        },
    },
    { key: "gtmOrganizationId", options: { suppressColumnsToolPanel: true, hide: true } },
    { key: "gtmOrganizationTenantId", options: { suppressColumnsToolPanel: true, hide: true } },
    {
        key: "gtmOrganizationName",
        options: {
            filterParams: { filterOptions: filterOptionsMap.gtmOrganizationName } as ITextFilterParams,
            cellRenderer: (value, params) => (
                <TenantInfoPopover tenantId={params.data.gtmOrganizationTenantId}>
                    <GridHighlightedValue {...params} columnKey="gtmOrganizationName" value={value} enableCopyToClipboard />
                </TenantInfoPopover>
            ),
        },
    },
    { key: "healthcareOrganizationId", options: { suppressColumnsToolPanel: true, hide: true } },
    { key: "healthcareOrganizationTenantId", options: { suppressColumnsToolPanel: true, hide: true } },
    {
        key: "healthcareOrganizationName",
        options: {
            filterParams: { filterOptions: filterOptionsMap.healthcareOrganizationName } as ITextFilterParams,
            cellRenderer: (value, params) => (
                <TenantInfoPopover tenantId={params.data.healthcareOrganizationTenantId}>
                    <GridHighlightedValue {...params} columnKey="healthcareOrganizationName" value={value} enableCopyToClipboard />
                </TenantInfoPopover>
            ),
        },
    },
    { key: "labOrganizationId", options: { suppressColumnsToolPanel: true, hide: true } },
    {
        key: "labOrganizationName",
        options: {
            filterParams: { filterOptions: filterOptionsMap.labOrganizationName } as ITextFilterParams,
            cellRenderer: (value, params) => (
                <TenantInfoPopover tenantId={params.data.labOrganizationTenantId}>
                    <GridHighlightedValue {...params} columnKey="labOrganizationName" value={value} enableCopyToClipboard />
                </TenantInfoPopover>
            ),
        },
    },
    {
        key: "samples",
        options: {
            filterParams: { filterOptions: filterOptionsMap.samples } as ITextFilterParams,
            onCellClicked: (e) => (e.node.master && e.data.samples ? e.node.setExpanded(!e.node.expanded) : undefined),
            cellRenderer: (value) => (
                <CopyToClipboard>
                    {value > 0 ? (
                        <span style={{ color: getGridLinkColor(), textDecoration: "none", cursor: "pointer", display: "block" }}>{value}</span>
                    ) : (
                        value
                    )}
                </CopyToClipboard>
            ),
        },
    },
    {
        key: "supplierOrganizationName",
        options: {
            filterParams: { filterOptions: filterOptionsMap.supplierOrganizationName } as ITextFilterParams,
            cellRenderer: (value, params) => (
                <TenantInfoPopover tenantId={params.data.supplierOrganizationTenantId}>
                    <GridHighlightedValue {...params} columnKey="supplierOrganizationName" value={value} enableCopyToClipboard />
                </TenantInfoPopover>
            ),
        },
    },
    {
        key: "kits",
        options: {
            filterParams: { filterOptions: filterOptionsMap.kits } as ITextFilterParams,
            onCellClicked: (e) => (e.node.master && e.data.kits ? e.node.setExpanded(!e.node.expanded) : undefined),
            cellRenderer: (value) => (
                <CopyToClipboard>
                    {value > 0 ? (
                        <span style={{ color: getGridLinkColor(), textDecoration: "none", cursor: "pointer", display: "block" }}>{value}</span>
                    ) : (
                        value
                    )}
                </CopyToClipboard>
            ),
        },
    },
    {
        key: "comments",
        options: {
            filterParams: { filterOptions: filterOptionsMap.comments } as ITextFilterParams,
            onCellClicked: (e) => (e.node.master && e.data.comments ? e.node.setExpanded(!e.node.expanded) : undefined),
            cellRenderer: (value) => (
                <CopyToClipboard>
                    {value > 0 ? (
                        <span style={{ color: getGridLinkColor(), textDecoration: "none", cursor: "pointer", display: "block" }}>{value}</span>
                    ) : (
                        value
                    )}
                </CopyToClipboard>
            ),
        },
    },

    ...(
        [
            "prescriberFirstName",
            "prescriberLastName",
            "prescriberNpiNumber",
            "creatorFirstName",
            "creatorLastName",
            // "customerName",
            "customerFirstName",
            "customerLastName",
            "customerEmail",
            "customerPhone",
        ] as (keyof OrderRowDataV3)[]
    ).map((colId) => ({
        key: colId,
        options: {
            filterParams: {
                filterOptions: filterOptionsMap[colId],
            },
            cellRenderer: (value, params) => (
                <span>
                    <GridHighlightedValue {...params} columnKey={colId} value={(value || value === 0) && value !== "" ? value : ""} enableCopyToClipboard />
                </span>
            ),
        },
    })),
    ...(["customerAddressStreet", "customerPostalCode", "customerAddressCity", "customerCountry", "customerCounty"] as (keyof OrderRowDataV3)[]).map(
        (colId) => ({
            key: colId,
            options: {
                headerTooltip: PATIENT_ADDRESS_TOOLTIP,
                filterParams: { filterOptions: filterOptionsMap[colId] },
                cellRenderer: (value, params) => (
                    <span>
                        <GridHighlightedValue {...params} columnKey={colId} value={(value || value === 0) && value !== "" ? value : ""} enableCopyToClipboard />
                    </span>
                ),
            },
        }),
    ),
    ...(
        [
            "patientFirstName",
            "patientLastName",
            "paymentType",
            "outcome",
            "patientEmail",
            "patientPhone",
            "patientSex",
            "patientRace",
            "patientEthnicity",
            "shippingPersonFirstName",
            "shippingPersonLastName",
            "shippingPersonEmail",
            "shippingPersonPhone",
            "shippingCity",
            "shippingState",
            "shippingPostalCode",
            "shippingAddressLine1",
            "shippingAddressLine2",
            "testProductName",
            "testProductLabCode",
            "testOfferedName",
            "testOfferedLabCode",
            "workflowCategory",
            "supplyProductName",
            "supplyProductSku",
        ] as (keyof OrderRowDataV3)[]
    ).map((colId) => ({
        key: colId,
        options: {
            filterParams: {
                filterOptions: filterOptionsMap[colId],
            },
            cellRenderer: (value, params) => (
                <span>
                    <GridHighlightedValue {...params} columnKey={colId} value={(value || value === 0) && value !== "" ? value : ""} enableCopyToClipboard />
                </span>
            ),
        },
    })),
    {
        key: "patientDateOfBirth",
        options: {
            filter: "agDateColumnFilter",
            filterParams: {
                filterOptions: filterOptionsMap.patientDateOfBirth,
                maxValidYear: 2100,
                minValidYear: 1900,
                inRangeFloatingFilterDateFormat: DatesFormat.defaultUSFormat,
            } as ITextFilterParams,
            cellRenderer: dateCellRenderer,
            valueGetter: (value) =>
                value.data.patientDateOfBirth && typeof value.data.patientDateOfBirth !== "undefined" && value.data.patientDateOfBirth !== DEFAULT_BOCORE_DATE
                    ? formatDateTo(value.data.patientDateOfBirth, DatesFormat.defaultUSFormat)
                    : "n/a",
        },
    },
    {
        key: "patientId",
        options: {
            filterParams: { filterOptions: filterOptionsMap.patientId } as ITextFilterParams,
            cellRenderer: (value, params) => {
                return (
                    <span>
                        <GridHighlightedValue {...params} columnKey="patientId" value={value || ""} enableCopyToClipboard />
                    </span>
                );
            },
        },
    },
    ...(["patientAddressStreet", "patientAddressCity", "patientPostalCode", "patientCounty", "patientCountry"] as (keyof OrderRowDataV3)[]).map((colId) => ({
        key: colId,
        options: {
            headerTooltip: PATIENT_ADDRESS_TOOLTIP,
            filterParams: { filterOptions: filterOptionsMap[colId] },
            cellRenderer: (value, params) => (
                <span>
                    <GridHighlightedValue {...params} columnKey={colId} value={(value || value === 0) && value !== "" ? value : ""} enableCopyToClipboard />
                </span>
            ),
        },
    })),
    {
        key: "resultPostedAt",
        options: {
            filter: "agDateColumnFilter",
            filterParams: {
                filterOptions: filterOptionsMap.resultPostedAt,
                maxValidYear: 2100,
                minValidYear: 1900,
                inRangeFloatingFilterDateFormat: DatesFormat.defaultUSFormat,
            } as ITextFilterParams,
            cellRenderer: (value, params) => {
                return !value ? "" : dateCellRenderer(value, params);
            },
        },
    },
    {
        key: "isUat",
        options: {
            filter: "agSetColumnFilter",
            sortable: false,
            filterParams: {
                values: [true, false],
                valueFormatter: (params: ValueFormatterParams) => (params.value ? "Yes" : "No"),
                suppressSelectAll: true,
                defaultToNothingSelected: true,
            },
            cellRenderer: (value) => <CopyToClipboard>{value ? "Yes" : "No"}</CopyToClipboard>,
        },
    },
    {
        key: "created",
        options: {
            filter: "agDateColumnFilter",
            filterParams: {
                filterOptions: filterOptionsMap.created,
                maxValidYear: 2100,
                minValidYear: 1900,
                inRangeFloatingFilterDateFormat: DatesFormat.defaultUSFormat,
            } as ITextFilterParams,
            cellRenderer: dateCellRenderer,
        },
    },
    {
        key: "updated",
        options: {
            filter: "agDateColumnFilter",
            filterParams: {
                filterOptions: filterOptionsMap.updated,
                maxValidYear: 2100,
                minValidYear: 1900,
                inRangeFloatingFilterDateFormat: DatesFormat.defaultUSFormat,
            } as IDateFilterParams,
            sort: "desc",
            cellRenderer: dateCellRenderer,
        },
    },
    {
        key: "workflowStepInProgressCanSubmit",
        options: {
            filter: "agSetColumnFilter",
            sortable: false,
            filterParams: {
                values: [true, false],
                valueFormatter: (params: ValueFormatterParams) => (params.value ? "Yes" : "No"),
                // suppressSelectAll: true,
            },
        },
    },
    {
        key: "labelTags",
        options: {
            sortable: false,
            filter: "agSetColumnFilter",
            filterParams: {
                values: (params) =>
                    getUniqueLabelTagsQuery(LabelTagType.JOURNEY, LabelTagOwnership.OWN).then((data) => {
                        // const filterValues = data.map((item: { id: number; name: string }) => {
                        //     return item.name;
                        // });
                        // @ts-ignore
                        params.success(data);
                    }),
                keyCreator: (item) => item.value.id,
                valueFormatter: (item) => item.value.name,
                showTooltips: true,
            } as ISetFilterParams,
            cellRenderer: (value) => (
                <CopyToClipboard>
                    <span style={{ display: "block" }}>{value?.map((e) => e.name).join(", ") || ""}</span>
                </CopyToClipboard>
            ),
        },
    },
    {
        key: "workflowStepInProgressTime",
        options: {
            filter: "agDateColumnFilter",
            filterParams: {
                // We have customized filter options for this column to change the display name from the default names mainly to indicate terms more closer to describe time in daily conversion
                // i.e date equals -> date since exactly
                filterOptions: [
                    {
                        displayKey: "equals",
                        displayName: "Since exactly",
                        predicate: (_filterValue, _cellValue) => {
                            // we return true here just to have the predicate function since its required for customed filter options as they need to confrom to this interface
                            // IFilterOptionDef
                            return true;
                        },
                    },
                    {
                        displayKey: "greaterThan",
                        displayName: "Since after",
                        predicate: (_filterValue, _cellValue) => {
                            // we return true here just to have the predicate function since its required for customed filter options as they need to confrom to this interface
                            // IFilterOptionDef
                            return true;
                        },
                    },
                    {
                        displayKey: "lessThan",
                        displayName: "Since before",
                        predicate: (_filterValue, _cellValue) => {
                            // we return true here just to have the predicate function since its required for customed filter options as they need to confrom to this interface
                            // IFilterOptionDef
                            return true;
                        },
                    },
                    {
                        displayKey: "inRange",
                        displayName: "Since within range",
                        predicate: (_filterValue, _cellValue) => {
                            // we return true here just to have the predicate function since its required for customed filter options as they need to confrom to this interface
                            // IFilterOptionDef
                            return true;
                        },
                        numberOfInputs: 2,
                    },
                ],
                inRangeFloatingFilterDateFormat: DatesFormat.defaultUSFormat,
            } as ITextFilterParams,
            cellRenderer: (value) => (
                <CopyToClipboard>
                    <Tooltip title={getDateDecreasedByDuration(value, "seconds")}>
                        <span>{value && value !== BO_CORE_DEFAULT_NUMBER_VALUE ? representTimeDuration(value, "seconds") : ""}</span>
                    </Tooltip>
                </CopyToClipboard>
            ),
        },
    },
];

export const requestFilterProcessorV3 = (params: IServerSideGetRowsRequest) => {
    const filterModelKeys = Object.keys(params.filterModel!);

    if (filterModelKeys.length === 0) {
        return [];
    }

    const result: any[] = filterModelKeys.map((filterKey) => {
        switch (filterKey) {
            case "workflowStepInProgressCanSubmit": {
                const filterValue = params.filterModel![filterKey]?.values[0];

                if (filterValue) {
                    return {
                        key: filterKey,
                        value: filterValue,
                        operator: "equals",
                    };
                }

                return undefined;
            }

            case "labelTags": {
                const filter = params.filterModel![filterKey].values;
                if (filter) {
                    return {
                        key: "tagId",
                        value: filter,
                        operator: "in",
                    };
                }

                // const filterValue = params.filterModel![filterKey]?.filter;
                // if (filterValue) {
                //     return {
                //         key: "labelTagName",
                //         value: filterValue,
                //         operator: params.filterModel![filterKey]?.type,
                //     };
                // }

                return undefined;
            }

            case "isUat": {
                // const filterValue = params.filterModel![filterKey]?.values[0];

                // if (filterValue) {
                //     return {
                //         key: filterKey,
                //         value: filterValue,
                //         operator: "equals",
                //     };
                // }

                return undefined;
            }

            default: {
                const singleFilterParams = params.filterModel![filterKey];

                const resultFilterItem = {
                    key: filterKey,
                    value: singleFilterParams?.filter,
                    operator: singleFilterParams?.type,
                };

                if (singleFilterParams.filterType !== "date") {
                    return resultFilterItem;
                }

                resultFilterItem.value = singleFilterParams?.dateFrom?.split(" ")[0];

                if (singleFilterParams.type === "inRange") {
                    resultFilterItem.value = {
                        startDate: singleFilterParams?.dateFrom?.split(" ")[0],
                        endDate: singleFilterParams?.dateTo?.split(" ")[0],
                    };
                }

                return resultFilterItem;
            }
        }
    });

    return result.filter((e) => e !== undefined);
};

const orderString = (value: any): string => (value && value !== BO_CORE_DEFAULT_VALUE ? value : "");
const orderCount = (value: any): number => (value && value !== BO_CORE_DEFAULT_NUMBER_VALUE ? value : 0);
const orderDate = (value: any): string => (value && value !== DEFAULT_BOCORE_DATE ? utcToLocalDateTime(value, true) : "");

export const getOrders = async (config?: any) => {
    const res = await getOrdersV3({}, { ...config, params: { ...config.params } }, false);

    const newResData = {
        ...res,
        data: {
            ...res.data,
            data: res.data.data.map((order): OrderRowDataV3 => ({
                id: order.id,
                masterOrderId: order.masterOrderId || "",
                updated: orderDate(order.updated),
                created: orderDate(order.created),
                resultPostedAt: orderDate(order.resultPostedAt),

                samples: orderCount(order.samples),
                kits: orderCount(order.kits),
                comments: orderCount(order.comments),

                isUat: order.isUat,

                testProductName: orderString(order.testProductName),
                testProductLabCode: orderString(order.testProductLabCode),

                supplyProductName: orderString(order.supplyProductName),
                supplyProductSku: orderString(order.supplyProductSku),

                testOfferedId: order.testOfferedId || null,
                testOfferedName: orderString(order.testOfferedName),
                testOfferedLabCode: orderString(order.testOfferedLabCode),

                orderingOrganizationId: order.orderingOrganizationId || null,
                orderingOrganizationName: orderString(order.orderingOrganizationName),

                orderingOrganizationTenantId: order.orderingOrganizationTenantId || null,
                orderingOrganizationAuthCode: orderString(order.orderingOrganizationAuthCode),

                gtmOrganizationId: order.gtmOrganizationId,
                gtmOrganizationName: orderString(order.gtmOrganizationName),
                gtmOrganizationTenantId: order.gtmOrganizationTenantId,
                healthcareOrganizationId: order.healthcareOrganizationId,
                healthcareOrganizationName: orderString(order.healthcareOrganizationName),
                healthcareOrganizationTenantId: order.healthcareOrganizationTenantId,

                labOrganizationId: order.labOrganizationId || null,
                labOrganizationName: orderString(order.labOrganizationName),
                labOrganizationTenantId: order.labOrganizationTenantId || null,

                supplierOrganizationId: order.supplierOrganizationId || null,
                supplierOrganizationName: orderString(order.supplierOrganizationName),
                supplierOrganizationTenantId: order.supplierOrganizationTenantId || null,

                paymentType: orderString(order.paymentType),

                prescriberId: order.prescriberId || null,
                prescriberNpiNumber: order.prescriberNpiNumber,
                prescriberFirstName: orderString(order.prescriberFirstName),
                prescriberLastName: orderString(order.prescriberLastName),

                creatorId: order.creatorId || null,
                creatorFirstName: orderString(order.creatorFirstName),
                creatorLastName: orderString(order.creatorLastName),

                customerFirstName: orderString(order.customerFirstName),
                customerLastName: orderString(order.customerLastName),
                customerEmail: orderString(order.customerEmail),
                customerPhone: orderString(order.customerPhone),
                customerAddressStreet: orderString(order.customerAddressStreet),
                customerPostalCode: orderString(order.customerPostalCode),
                customerAddressCity: orderString(order.customerAddressCity),
                customerCountry: orderString(order.customerCountry),
                customerCounty: orderString(order.customerCounty),

                shippingPersonFirstName: orderString(order.shippingPersonFirstName),
                shippingPersonLastName: orderString(order.shippingPersonLastName),
                shippingPersonEmail: orderString(order.shippingPersonEmail),
                shippingPersonPhone: orderString(order.shippingPersonPhone),

                shippingCity: orderString(order.shippingCity),
                shippingState: orderString(order.shippingState),
                shippingPostalCode: orderString(order.shippingPostalCode),
                shippingAddressLine1: orderString(order.shippingAddressLine1),
                shippingAddressLine2: orderString(order.shippingAddressLine2),

                patientId: order.patientId || null,
                patientSex: orderString(order.patientSex),
                patientRace: orderString(order.patientRace),
                patientEmail: orderString(order.patientEmail),
                patientPhone: orderString(order.patientPhone),
                patientLastName: orderString(order.patientLastName),
                patientFirstName: orderString(order.patientFirstName),
                patientEthnicity: orderString(order.patientEthnicity),
                patientDateOfBirth: orderString(order.patientDateOfBirth),
                patientCounty: orderString(order.patientCounty),
                patientCountry: orderString(order.patientCountry),
                patientPostalCode: orderString(order.patientPostalCode),
                patientAddressCity: orderString(order.patientAddressCity),
                patientAddressStreet: orderString(order.patientAddressStreet),

                outcome: orderString(order.outcome),

                workflowStepInProgressId: order.workflowStepInProgressId || null,
                workflowStepInProgressType: orderString(order.workflowStepInProgressType),
                workflowStepInProgressName: orderString(order.workflowStepInProgressName),
                workflowStepInProgressTime: order.workflowStepInProgressTime || null,
                workflowStepInProgressCanSubmit: order.workflowStepInProgressCanSubmit,
                workflowCategory: orderString(order.workflowCategory),

                journeyId: order.journeyId || null,

                labelTags: order.labelTags,
            })),
        },
    };
    return newResData;
};

export const allColumnsRequired = {
    testOffered: true,
    testProduct: true,
    supplyProduct: true,
    samples: true,
    kits: true,
    comments: true,
    orderingOrganization: true,
    gtmOrganization: true,
    healthcareOrganization: true,
    labOrganization: true,
    supplierOrganization: true,
    paymentType: true,
    resultPostedAt: true,
    prescriber: true,
    creator: true,
    customer: true,
    shippingPerson: true,
    shippingAddress: true,
    patient: true,
    workflowStepInProgress: true,
};

export const getCustomOrderV3Type = (requirements: CustomColumnsRequirements): IOrderGridCystomColumnDefinition[] => {
    const result: IOrderGridCystomColumnDefinition[] = [
        { attrKey: "id", atrbType: "number", name: "ID" },
        { attrKey: "masterOrderId", atrbType: "number", name: "Master Order ID" },
        { attrKey: "orderingOrganizationId", atrbType: "text", name: "Ordering Organization Id" },
        { attrKey: "outcome", atrbType: "text", name: "Order Status" },
    ];

    if (requirements.orderingOrganization) {
        result.push({ attrKey: "orderingOrganizationName", atrbType: "text", name: "Ordering Organization" });
    }

    result.push({ attrKey: "labOrganizationId", atrbType: "text", name: "Lab Organization Id" });
    if (requirements.labOrganization) {
        result.push({ attrKey: "labOrganizationName", atrbType: "text", name: "Lab Organization" });
    }

    result.push({ attrKey: "gtmOrganizationId", atrbType: "text", name: "Go To Market Organization Id" });
    if (requirements.gtmOrganization) {
        result.push(
            { attrKey: "gtmOrganizationTenantId", atrbType: "text", name: "Go To Market Tenant Id" },
            { attrKey: "gtmOrganizationName", atrbType: "text", name: "Go To Market Organization" },
        );
    }

    result.push({ attrKey: "healthcareOrganizationId", atrbType: "text", name: "Healthcare Organization Id" });
    if (requirements.healthcareOrganization) {
        result.push(
            { attrKey: "healthcareOrganizationTenantId", atrbType: "text", name: "Healthcare Tenant Id" },
            { attrKey: "healthcareOrganizationName", atrbType: "text", name: "Healthcare Organization" },
        );
    }

    if (requirements.labOrganization || requirements.healthcareOrganization || requirements.gtmOrganization) {
        result.push({ attrKey: "labelTags", atrbType: "text", name: "Tags" });
    }

    result.push({ attrKey: "supplierOrganizationId", atrbType: "text", name: "Supplier Organization Id" });
    if (requirements.supplierOrganization) {
        result.push({ attrKey: "supplierOrganizationName", atrbType: "text", name: "Supplier Organization" });
    }

    if (requirements.prescriber) {
        result.push(
            { attrKey: "prescriberFirstName", atrbType: "text", name: "Provider First Name" },
            { attrKey: "prescriberLastName", atrbType: "text", name: "Provider Last Name" },
            { attrKey: "prescriberNpiNumber", atrbType: "number", name: "Provider NPI" },
        );
    }

    if (requirements.creator) {
        result.push(
            { attrKey: "creatorFirstName", atrbType: "text", name: "Creator First Name" },
            { attrKey: "creatorLastName", atrbType: "text", name: "Creator Last Name" },
        );
    }

    if (requirements.customer) {
        result.push(
            { attrKey: "customerFirstName", atrbType: "text", name: "Customer First Name" },
            { attrKey: "customerLastName", atrbType: "text", name: "Customer Last Name" },
            { attrKey: "customerEmail", atrbType: "text", name: "Customer Email" },
            { attrKey: "customerPhone", atrbType: "text", name: "Customer Phone" },

            { attrKey: "customerAddressStreet", atrbType: "text", name: "Customer Address Street" },
            { attrKey: "customerPostalCode", atrbType: "text", name: "Customer Postal Code" },
            { attrKey: "customerAddressCity", atrbType: "text", name: "Customer Address City" },
            { attrKey: "customerCountry", atrbType: "text", name: "Customer Country" },
            { attrKey: "customerCounty", atrbType: "text", name: "Customer County" },
        );
    }

    result.push({ attrKey: "patientId", atrbType: "number", name: "Patient ID" });
    if (requirements.patient) {
        result.push(
            { attrKey: "patientFirstName", atrbType: "text", name: "Patient First Name" },
            { attrKey: "patientLastName", atrbType: "text", name: "Patient Last Name" },
            { attrKey: "patientDateOfBirth", atrbType: "timestamp", name: "Patient Date Of Birth" },
            { attrKey: "patientEmail", atrbType: "text", name: "Patient Email" },
            { attrKey: "patientPhone", atrbType: "text", name: "Patient Phone" },
            { attrKey: "patientSex", atrbType: "text", name: "Patient Sex" },
            { attrKey: "patientRace", atrbType: "text", name: "Patient Race" },
            { attrKey: "patientEthnicity", atrbType: "text", name: "Patient Ethnicity" },
            { attrKey: "patientAddressStreet", atrbType: "text", name: "Patient Address Street" },
            { attrKey: "patientAddressCity", atrbType: "text", name: "Patient Address City" },
            { attrKey: "patientPostalCode", atrbType: "text", name: "Patient Postal Code" },
            { attrKey: "patientCounty", atrbType: "text", name: "Patient County" },
            { attrKey: "patientCountry", atrbType: "text", name: "Patient Country" },
        );
    }

    if (requirements.paymentType) {
        result.push({ attrKey: "paymentType", atrbType: "text", name: "Payment Type" });
    }

    if (requirements.samples) {
        result.push({ attrKey: "samples", atrbType: "text", name: "Samples" });
    }

    if (requirements.kits) {
        result.push({ attrKey: "kits", atrbType: "text", name: "Kits" });
    }

    if (requirements.comments) {
        result.push({ attrKey: "comments", atrbType: "text", name: "Comments" });
    }

    if (requirements.shippingPerson) {
        result.push(
            { attrKey: "shippingPersonFirstName", atrbType: "text", name: "Shipping Person First Name" },
            { attrKey: "shippingPersonLastName", atrbType: "text", name: "Shipping Person Last Name" },
            { attrKey: "shippingPersonEmail", atrbType: "text", name: "Shipping Person Email" },
            { attrKey: "shippingPersonPhone", atrbType: "text", name: "Shipping Person Phone" },
        );
    }

    if (requirements.shippingAddress) {
        result.push(
            { attrKey: "shippingCity", atrbType: "text", name: "Shipping City" },
            { attrKey: "shippingState", atrbType: "text", name: "Shipping State" },
            { attrKey: "shippingPostalCode", atrbType: "text", name: "Shipping Postal Code" },
            { attrKey: "shippingAddressLine1", atrbType: "text", name: "Shipping Address Line 1" },
            { attrKey: "shippingAddressLine2", atrbType: "text", name: "Shipping Address Line 2" },
        );
    }

    if (requirements.testProduct) {
        result.push(
            { attrKey: "testProductName", atrbType: "text", name: "Product" },
            { attrKey: "testProductLabCode", atrbType: "text", name: "Product Lab Code" },
        );
    }

    if (requirements.testOffered) {
        result.push(
            { attrKey: "testOfferedName", atrbType: "text", name: "Test Ordered" },
            { attrKey: "testOfferedLabCode", atrbType: "text", name: "Test Ordered Lab Code" },
        );
    }

    result.push({ attrKey: "workflowCategory", atrbType: "text", name: "Workflow Category" });

    if (requirements.supplyProduct) {
        result.push({ attrKey: "supplyProductName", atrbType: "text", name: "Supply" }, { attrKey: "supplyProductSku", atrbType: "text", name: "Supply SKU" });
    }

    if (requirements.workflowStepInProgress) {
        result.push(
            { attrKey: "workflowStepInProgressName", atrbType: "text", name: "Step In Progress Name" },
            { attrKey: "workflowStepInProgressType", atrbType: "text", name: "Step In Progress Type" },
            { attrKey: "workflowStepInProgressTime", atrbType: "date", name: "Time On This Step" },
            { attrKey: "workflowStepInProgressCanSubmit", atrbType: "boolean", name: "Workflow Step In Progress Can Submit" },
        );
    }

    if (requirements.resultPostedAt) {
        result.push({ attrKey: "resultPostedAt", atrbType: "timestamp", name: "Resulted Date" });
    }

    result.push(
        { attrKey: "isUat", atrbType: "boolean", name: "UAT Order" },
        { attrKey: "created", atrbType: "timestamp", name: "Order Date" },
        { attrKey: "updated", atrbType: "timestamp", name: "Updated" },
    );

    return result;
};
